import { authenticatedFetch } from "./api";
import { refreshTokenIfNewlyVerified } from "./emailVerification";
import { readStored, removeStored, writeStored } from "./safeStorage";
import { forgetUrlSecret, readUrlSecret } from "./urlSecrets";

/**
 * ENG-3832 — a demo org claim link: `https://account.starthaven.com/#claim=<token>`, minted by Haven
 * staff. Whoever claims it gets a free Business organization they own, named by staff.
 *
 * The token is a one-time secret, so it goes nowhere it could be recorded:
 * - out of the address bar before any analytics tag loads (public/index.html keeps it in
 *   `window.__havenUrlSecrets.claim`; [claimTokenFromLocation] covers the case where that script did
 *   not run);
 * - to the backend in a POST body only, never a URL, so it is in no request log;
 * - into no analytics event, and no console log.
 *
 * It is saved in this browser ([saveClaimToken]) so it survives signing in or signing up, including
 * the verification email opening a new tab. The saved copy is a session key (lib/userSession): cleared
 * at sign-out, so the next person on a shared browser does not inherit it.
 */

export const ORG_CLAIM_STORAGE_KEY = "pendingOrgClaim";

/** As long as an invitation is remembered (useJoinInvitation). The link itself may expire sooner. */
const SAVED_FOR_MS = 7 * 24 * 60 * 60 * 1000;

/** The token from a `#claim=<token>` hash, or null. */
export function claimTokenFromHash(hash: string): string | null {
  if (!hash.startsWith("#claim=")) return null;
  const token = hash.slice("#claim=".length).trim();
  return token ? token : null;
}

/** The token this page was opened with: where index.html put it, else the hash itself. */
export function claimTokenFromLocation(): string | null {
  return readUrlSecret("claim") || claimTokenFromHash(window.location.hash);
}

export function saveClaimToken(token: string, now: number = Date.now()): void {
  writeStored(ORG_CLAIM_STORAGE_KEY, JSON.stringify({ token, expires: now + SAVED_FOR_MS }));
}

/** A saved token that has not run out, or null. A stale or unreadable one is removed. */
export function readSavedClaimToken(now: number = Date.now()): string | null {
  const raw = readStored(ORG_CLAIM_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { token?: unknown; expires?: unknown };
    if (typeof parsed.token === "string" && parsed.token && typeof parsed.expires === "number" && parsed.expires > now) {
      return parsed.token;
    }
  } catch {
    // Unreadable: treated as nothing saved.
  }
  removeStored(ORG_CLAIM_STORAGE_KEY);
  return null;
}

/** The link this visit should offer: the one it was opened with, else one saved before signing in. */
export function readClaimToken(): string | null {
  return claimTokenFromLocation() || readSavedClaimToken();
}

/** Done with the link, claimed or not: forget every copy this page holds. */
export function forgetClaimToken(): void {
  removeStored(ORG_CLAIM_STORAGE_KEY);
  forgetUrlSecret("claim");
}

/** The backend's one answer for a link that can't be used, whatever the reason (OrgClaimRoutes.kt). */
const UNAVAILABLE_CODE = "claim_link_unavailable";
const UNVERIFIED_CODE = "email_unverified";

export const CLAIM_UNAVAILABLE_MESSAGE =
  "This link can't be used. It may have expired or already been used. Ask whoever sent it for a new one.";
export const CLAIM_UNREACHABLE_MESSAGE = "Couldn't reach Haven. Check your connection and try again.";

export type ClaimPreview =
  | { kind: "ready"; orgName: string }
  /** This account claimed the link before (a lost response, another tab): open the org it made. */
  | { kind: "alreadyClaimed"; tenantId: string }
  /** The organization rules would refuse this account; the same answer the claim gives. */
  | { kind: "refused"; message: string; ownedTenantId?: string }
  | { kind: "unavailable"; message: string }
  | { kind: "unverified"; message: string }
  | { kind: "error"; message: string };

export type ClaimResult =
  | { kind: "claimed"; tenantId: string; name: string }
  | { kind: "unavailable"; message: string }
  | { kind: "unverified"; message: string }
  /** The organization rules refused this account (already in a company, already owns one, on a family). */
  | { kind: "refused"; message: string; ownedTenantId?: string }
  | { kind: "error"; message: string };

type Body = { error?: unknown; code?: unknown; orgName?: unknown; tenantId?: unknown; name?: unknown; claimable?: unknown };

const text = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v : undefined);

async function post(path: string, token: string): Promise<{ status: number; body: Body } | null> {
  try {
    // The verified claim lives in the ID token, so an email verified since sign-in needs a fresh one.
    await refreshTokenIfNewlyVerified();
    const res = await authenticatedFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const body = (await res.json().catch(() => ({}))) as Body;
    return { status: res.status, body: body ?? {} };
  } catch {
    return null;
  }
}

/** The organization rules' 409, the same from the preview and the claim (OrgClaimRoutes.kt). */
function refusal(body: Body): { kind: "refused"; message: string; ownedTenantId?: string } {
  const tenantId = text(body.tenantId);
  return {
    kind: "refused",
    message: text(body.error) ?? "This account can't create the organization.",
    ...(body.code === "already_owns_org" && tenantId ? { ownedTenantId: tenantId } : {}),
  };
}

/** What the link would create, for this account. Reveals nothing about a link that can't be used. */
export async function previewClaim(token: string): Promise<ClaimPreview> {
  const res = await post("/org-claim-links/preview", token);
  if (!res) return { kind: "error", message: CLAIM_UNREACHABLE_MESSAGE };
  const { status, body } = res;
  const orgName = text(body.orgName);
  const tenantId = text(body.tenantId);
  if (status === 200 && body.claimable === false && tenantId) return { kind: "alreadyClaimed", tenantId };
  if (status === 200 && orgName) return { kind: "ready", orgName };
  if (body.code === UNVERIFIED_CODE) return { kind: "unverified", message: text(body.error) ?? "Verify your email address first." };
  if (status === 404 || body.code === UNAVAILABLE_CODE) return { kind: "unavailable", message: CLAIM_UNAVAILABLE_MESSAGE };
  if (status === 409) return refusal(body);
  return { kind: "error", message: text(body.error) ?? "Something went wrong. Try again." };
}

/** Create the organization the link names, owned by this account. */
export async function claimOrg(token: string): Promise<ClaimResult> {
  const res = await post("/org-claim-links/claim", token);
  if (!res) return { kind: "error", message: CLAIM_UNREACHABLE_MESSAGE };
  const { status, body } = res;
  const tenantId = text(body.tenantId);
  // 200: this account had claimed it already (a retry), and the body names the org it made.
  if ((status === 201 || status === 200) && tenantId) return { kind: "claimed", tenantId, name: text(body.name) ?? "" };
  if (body.code === UNVERIFIED_CODE) return { kind: "unverified", message: text(body.error) ?? "Verify your email address first." };
  if (status === 404 || body.code === UNAVAILABLE_CODE) return { kind: "unavailable", message: CLAIM_UNAVAILABLE_MESSAGE };
  if (status === 409) return refusal(body);
  return { kind: "error", message: text(body.error) ?? "Something went wrong creating your organization. Try again." };
}
